import { Link } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { searchCustomers, updateCustomer, createCustomer, deleteCustomer, bulkDeleteCustomers } from "../../api/customers";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Spinner from "../common/Spinner";
import { formatEasternDate } from "../../utils/dates";

export default function CustomerManagement() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const loadCustomers = useCallback(async (query = "") => {
    if (query.length < 2 && query.length > 0) return;
    
    setLoading(true);
    try {
      if (query.length === 0) {
        // Load all customers (or recent ones)
        const results = await searchCustomers("", 100);
        setCustomers(results);
      } else {
        const results = await searchCustomers(query, 50);
        setCustomers(results);
      }
    } catch (error) {
      console.error("Failed to load customers:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadCustomers(searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, loadCustomers]);

  const handleEdit = async (customer) => {
    setSelectedCustomer(customer);
    setEditModalOpen(true);
  };

  const handleDelete = async (customer) => {
    setSelectedCustomer(customer);
    setDeleteModalOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    setDeleteModalOpen(true);
  };

  const toggleSelection = (customerId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(customerId)) {
      newSelected.delete(customerId);
    } else {
      newSelected.add(customerId);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    if (selectedIds.size === customers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(customers.map(c => c.id)));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-accent text-2xl font-bold text-birdie-charcoal">Customer Management</h2>
          <p className="text-birdie-charcoal/70">Manage your customer database</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setCreateModalOpen(true)}
            className="bg-birdie-green hover:bg-birdie-green-dark"
          >
            <PlusIcon />
            Add Customer
          </Button>
          <Button
            variant="secondary"
            onClick={() => setBulkDeleteMode(!bulkDeleteMode)}
            className={bulkDeleteMode ? "bg-red-50 text-red-700" : ""}
          >
            {bulkDeleteMode ? "Cancel Bulk" : "Bulk Actions"}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search customers by name, email, phone, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-birdie-green/15 px-4 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
          />
        </div>
        {bulkDeleteMode && selectedIds.size > 0 && (
          <Button
            onClick={handleBulkDelete}
            variant="secondary"
            className="bg-red-50 text-red-700 hover:bg-red-100"
          >
            Delete Selected ({selectedIds.size})
          </Button>
        )}
      </div>

      {/* Bulk selection header */}
      {bulkDeleteMode && (
        <div className="flex items-center justify-between rounded-lg bg-birdie-cream p-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={customers.length > 0 && selectedIds.size === customers.length}
              onChange={selectAll}
              className="rounded border-birdie-green/25"
            />
            <span className="text-sm text-birdie-charcoal/70">
              {selectedIds.size === 0 ? "Select customers" : `${selectedIds.size} selected`}
            </span>
          </div>
          <Button
            variant="link"
            onClick={() => {
              setBulkDeleteMode(false);
              setSelectedIds(new Set());
            }}
            className="text-birdie-green"
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Customer List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner label="Loading customers..." />
        </div>
      ) : customers.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-birdie-green/25 p-8 sm:p-12 text-center">
          <UsersIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-birdie-warmgray" />
          <h3 className="mt-3 sm:mt-4 text-base sm:text-lg font-medium text-birdie-charcoal">No customers found</h3>
          <p className="mt-2 text-sm text-birdie-charcoal/60">
            {searchTerm ? "Try adjusting your search terms" : "Get started by adding your first customer"}
          </p>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {customers.map((customer) => (
              <div
                key={customer.id}
                className={`rounded-xl border bg-white p-4 shadow-sm ${
                  bulkDeleteMode && selectedIds.has(customer.id) ? "border-birdie-green bg-birdie-cream" : "border-birdie-green/15"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {bulkDeleteMode && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(customer.id)}
                        onChange={() => toggleSelection(customer.id)}
                        className="rounded border-birdie-green/25 mt-1"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <Link to={`/admin/customers/${customer.id}`} className="font-medium text-birdie-charcoal hover:underline">
                        {customer.full_name}
                      </Link>
                      {customer.email && (
                        <div className="text-sm text-birdie-charcoal/70 truncate">{customer.email}</div>
                      )}
                      {customer.phone && (
                        <div className="text-sm text-birdie-charcoal/60">{customer.phone}</div>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="inline-flex items-center rounded-full bg-birdie-green/10 px-2 py-0.5 text-xs font-medium text-birdie-green">
                          {customer.booking_count || 0} bookings
                        </span>
                        {customer.company_name && (
                          <span className="text-xs text-birdie-charcoal/60 truncate">{customer.company_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => handleEdit(customer)}
                      className="text-birdie-green hover:text-birdie-green-dark text-xs"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => handleDelete(customer)}
                      className="text-red-600 hover:text-red-900 text-xs"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-hidden rounded-lg border border-birdie-green/15 bg-white shadow">
            <table className="min-w-full divide-y divide-birdie-green/15">
              <thead className="bg-birdie-beige">
                <tr>
                  {bulkDeleteMode && (
                    <th className="w-12 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={customers.length > 0 && selectedIds.size === customers.length}
                        onChange={selectAll}
                        className="rounded border-birdie-green/25"
                      />
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-birdie-charcoal/60">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-birdie-charcoal/60">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-birdie-charcoal/60">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-birdie-charcoal/60">
                    Bookings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-birdie-charcoal/60">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-birdie-charcoal/60">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-birdie-green/15 bg-white">
                {customers.map((customer) => (
                  <tr
                    key={customer.id}
                    className={`hover:bg-birdie-beige ${
                      bulkDeleteMode && selectedIds.has(customer.id) ? "bg-birdie-cream" : ""
                    }`}
                  >
                    {bulkDeleteMode && (
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(customer.id)}
                          onChange={() => toggleSelection(customer.id)}
                          className="rounded border-birdie-green/25"
                        />
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <Link
                          to={`/admin/customers/${customer.id}`}
                          className="text-sm font-medium text-birdie-charcoal hover:underline"
                        >
                          {customer.full_name}
                        </Link>
                        <div className="text-sm text-birdie-charcoal/60">ID: {customer.id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-birdie-charcoal">
                      <div>
                        {customer.email && (
                          <div className="flex items-center gap-1">
                            <EmailIcon className="h-4 w-4 text-birdie-warmgray" />
                            {customer.email}
                          </div>
                        )}
                        {customer.phone && (
                          <div className="flex items-center gap-1 mt-1">
                            <PhoneIcon className="h-4 w-4 text-birdie-warmgray" />
                            {customer.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-birdie-charcoal">
                      {customer.company_name || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center rounded-full bg-birdie-green/10 px-2.5 py-0.5 text-xs font-medium text-birdie-green">
                        {customer.booking_count || 0} bookings
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-birdie-charcoal/60">
                      {formatEasternDate(customer.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="link"
                          onClick={() => handleEdit(customer)}
                          className="text-birdie-green hover:text-birdie-green-dark"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="link"
                          onClick={() => handleDelete(customer)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modals */}
      <CustomerEditModal
        isOpen={editModalOpen}
        customer={selectedCustomer}
        onClose={(updated) => {
          setEditModalOpen(false);
          setSelectedCustomer(null);
          if (updated) loadCustomers(searchTerm);
        }}
      />

      <CustomerDeleteModal
        isOpen={deleteModalOpen}
        customer={selectedCustomer}
        selectedIds={selectedIds}
        onClose={(deleted) => {
          setDeleteModalOpen(false);
          setSelectedCustomer(null);
          if (deleted) {
            loadCustomers(searchTerm);
            setSelectedIds(new Set());
            setBulkDeleteMode(false);
          }
        }}
      />

      <CustomerCreateModal
        isOpen={createModalOpen}
        onClose={(created) => {
          setCreateModalOpen(false);
          if (created) loadCustomers(searchTerm);
        }}
      />
    </div>
  );
}

// Edit Modal Component
function CustomerEditModal({ isOpen, customer, onClose }) {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    company_name: ""
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (customer) {
      setFormData({
        full_name: customer.full_name || "",
        email: customer.email || "",
        phone: customer.phone || "",
        company_name: customer.company_name || ""
      });
    }
    setError("");
  }, [customer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name.trim()) {
      setError("Name is required");
      return;
    }

    setSaving(true);
    setError("");
    
    try {
      await updateCustomer(customer.id, formData);
      onClose(true);
    } catch (err) {
      setError(err.message || "Failed to update customer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => onClose(false)} title="Edit Customer">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium text-birdie-charcoal">Name *</label>
          <input
            type="text"
            required
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-birdie-charcoal">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-birdie-charcoal">Phone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-birdie-charcoal">Company/Venue</label>
          <input
            type="text"
            value={formData.company_name}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
            className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Save Changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Delete Modal Component  
function CustomerDeleteModal({ isOpen, customer, selectedIds, onClose }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [force, setForce] = useState(false);

  const isMultiple = selectedIds && selectedIds.size > 0;
  const count = isMultiple ? selectedIds.size : 1;
  const hasBookings = customer?.booking_count > 0;

  useEffect(() => {
    setError("");
    setForce(false);
  }, [isOpen]);

  const handleDelete = async () => {
    setDeleting(true);
    setError("");

    try {
      if (isMultiple) {
        await bulkDeleteCustomers(Array.from(selectedIds), force);
      } else {
        await deleteCustomer(customer.id);
      }
      onClose(true);
    } catch (err) {
      setError(err.message || "Failed to delete customer(s)");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => onClose(false)} title="Delete Customer">
      <div className="space-y-4">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <WarningIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Delete {count} customer{count > 1 ? 's' : ''}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                {isMultiple ? (
                  <p>This will permanently delete {count} customers.</p>
                ) : (
                  <p>
                    This will permanently delete <strong>{customer?.full_name}</strong>
                    {hasBookings && (
                      <span> and their {customer.booking_count} booking(s)</span>
                    )}.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {(hasBookings || isMultiple) && (
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
                className="rounded border-birdie-green/25"
              />
              <span className="text-sm text-birdie-charcoal">
                Also delete associated bookings
              </span>
            </label>
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {deleting ? <Spinner size="sm" /> : `Delete Customer${count > 1 ? 's' : ''}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Create Modal Component
function CustomerCreateModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    company_name: ""
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setFormData({
        full_name: "",
        email: "",
        phone: "",
        company_name: ""
      });
      setError("");
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.full_name.trim()) {
      setError("Name is required");
      return;
    }

    setSaving(true);
    setError("");
    
    try {
      await createCustomer(formData);
      onClose(true);
    } catch (err) {
      setError(err.message || "Failed to create customer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => onClose(false)} title="Add New Customer">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium text-birdie-charcoal">Name *</label>
          <input
            type="text"
            required
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-birdie-charcoal">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-birdie-charcoal">Phone</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-birdie-charcoal">Company/Venue</label>
          <input
            type="text"
            value={formData.company_name}
            onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
            className="mt-1 block w-full rounded-md border border-birdie-green/15 px-3 py-2 focus:border-birdie-green focus:ring-birdie-mustard/40"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="secondary" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Add Customer"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Icons
function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function UsersIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
    </svg>
  );
}

function EmailIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function PhoneIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function WarningIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  );
}
